import { forwardRef, useState, useRef, useEffect, useCallback, useLayoutEffect, type SelectHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '@admin/shared/components/Icons';

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: ReactNode;
  helperText?: string;
  error?: string;
  options: { value: string; label: string }[];
  onChange?: (e: { target: { value: string } }) => void;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, helperText, error, options, className = '', id, value, onChange, disabled, ...props }, ref) => {
    const uid = id || (label && typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const [open, setOpen] = useState(false);
    const [highlightIdx, setHighlightIdx] = useState(-1);
    const [menuRect, setMenuRect] = useState<{ left: number; top: number; width: number; openUp: boolean } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const selectedOption = options.find(o => String(o.value) === String(value));
    const displayLabel = selectedOption?.label ?? (options[0]?.label || '');

    // Position the portal menu relative to the trigger; track scroll/resize.
    useLayoutEffect(() => {
      if (!open) return;
      const update = () => {
        const t = triggerRef.current;
        if (!t) return;
        const r = t.getBoundingClientRect();
        const menuMax = 240; // matches max-h-60 (15rem)
        const spaceBelow = window.innerHeight - r.bottom;
        const openUp = spaceBelow < Math.min(menuMax, 200) && r.top > spaceBelow;
        setMenuRect({
          left: r.left,
          top: openUp ? r.top : r.bottom,
          width: r.width,
          openUp,
        });
      };
      update();
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      return () => {
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      };
    }, [open]);

    // Close on outside click (account for portal-rendered listbox)
    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        const target = e.target as Node;
        if (containerRef.current && containerRef.current.contains(target)) return;
        if (listRef.current && listRef.current.contains(target)) return;
        setOpen(false);
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (open && highlightIdx >= 0) {
          onChange?.({ target: { value: options[highlightIdx].value } });
          setOpen(false);
        } else {
          setOpen(o => !o);
          setHighlightIdx(options.findIndex(o => String(o.value) === String(value)));
        }
      } else if (e.key === 'Escape') {
        setOpen(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!open) { setOpen(true); setHighlightIdx(0); return; }
        setHighlightIdx(i => Math.min(i + 1, options.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx(i => Math.max(i - 1, 0));
      }
    }, [disabled, open, highlightIdx, options, value, onChange]);

    // Scroll highlighted item into view
    useEffect(() => {
      if (!open || highlightIdx < 0 || !listRef.current) return;
      const item = listRef.current.children[highlightIdx] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }, [highlightIdx, open]);

    return (
      <div className="space-y-1.5" ref={containerRef}>
        {label && (
          <label htmlFor={uid} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </label>
        )}
        {/* Hidden native select for form semantics / ref forwarding */}
        <select ref={ref} id={uid} value={value} tabIndex={-1} aria-hidden="true"
          className="sr-only" onChange={e => onChange?.({ target: { value: e.target.value } })} {...props}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {/* Custom trigger + dropdown wrapper — relative positioning, no portal needed */}
        <div className="relative">
          <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={uid ? `${uid}-listbox` : undefined}
            disabled={disabled}
            onClick={() => !disabled && setOpen(o => !o)}
            onKeyDown={handleKeyDown}
            className={`
              relative w-full h-10 px-3 pr-9 text-sm text-left rounded-xl border bg-white dark:bg-gray-900
              text-gray-900 dark:text-white cursor-pointer
              ${open
                ? 'border-indigo-400 dark:border-indigo-500/60 ring-2 ring-indigo-500/20 dark:ring-indigo-400/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-500/40'}
              focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-400/20 focus:border-indigo-400 dark:focus:border-indigo-500/60
              disabled:opacity-50 disabled:cursor-not-allowed
              ${error ? 'border-red-400 dark:border-red-500 focus:ring-red-500/20' : ''}
              ${className}
            `}
          >
            <span className="block truncate">{displayLabel}</span>
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center w-5 h-5 rounded-md bg-indigo-50 dark:bg-indigo-900/30">
              <Icons.ChevronDown className={`w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
            </span>
          </button>

          {/* Dropdown list — portaled with fixed positioning so it escapes any overflow/transform parents */}
          {open && menuRect && createPortal(
            <ul
              ref={listRef}
              id={uid ? `${uid}-listbox` : undefined}
              role="listbox"
              style={{
                position: 'fixed',
                left: menuRect.left,
                top: menuRect.openUp ? undefined : menuRect.top + 4,
                bottom: menuRect.openUp ? window.innerHeight - menuRect.top + 4 : undefined,
                width: menuRect.width,
                zIndex: 1000,
              }}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-auto focus:outline-none max-h-60 animate-[selectIn_0.12s_ease-out]"
            >
              {options.map((opt, idx) => {
                const isSelected = String(opt.value) === String(value);
                const isHighlighted = idx === highlightIdx;
                return (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    className={`
                      flex items-center justify-between px-3 h-9 text-sm cursor-pointer select-none
                      ${isHighlighted ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''}
                      ${isSelected && !isHighlighted ? 'bg-gray-50 dark:bg-gray-800' : ''}
                      ${!isHighlighted && !isSelected ? 'hover:bg-gray-50 dark:hover:bg-gray-800' : ''}
                      text-gray-900 dark:text-white
                    `}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    onMouseDown={e => {
                      e.preventDefault();
                      onChange?.({ target: { value: opt.value } });
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && <Icons.Check className="w-4 h-4 text-indigo-500 shrink-0 ml-2" />}
                  </li>
                );
              })}
            </ul>,
            document.body
          )}
        </div>

        {error && <p className="text-xs text-red-500 dark:text-red-400">{error}</p>}
        {helperText && !error && <p className="text-xs text-gray-400 dark:text-gray-500">{helperText}</p>}
      </div>
    );
  }
);
Select.displayName = 'Select';
